import { FC, useState } from 'react';
import { CompanyAWS } from '../../../@types/AWS';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CompanyAttributes, License, LicenseStatusTypes, LicenseTypes } from '../../../@types/Company';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '../../common/Modal/Modal';
import Form from '../../common/Form/Form';
import { FormProvider, } from 'react-hook-form';
import { Stack, Typography } from '@mui/material';
import LoadingButton from '../../common/LoadingButton/LoadingButton';
import Button from '../../common/Button';
import axios from '../../../api/axios';
import CheckBox from '../../common/Form/FormFields/CheckBox/CheckBox';
import TextField from '../../common/Form/FormFields/TextField/TextField';
import flatten from 'flat';
import { ChassisSeriesTypes } from '../../../@types/ChassisType';
import { HorizontalRule } from '@mui/icons-material';
import ChassisSeriesTypeOptions from '../../common/Form/ChassisSeriesTypeOptions/ChassisSeriesTypeOptions';
import getObjectDifferences from '../../../utils/getObjectDifference';
import useAuthentication from '../../../hooks/useAuthentication';

interface EditAttributesModalProps {
  company: CompanyAWS;
  closeModal: () => void;
  isVisible: boolean;
}

const EditAttributesModal: FC<EditAttributesModalProps> = ({ company, closeModal, isVisible }) => {

  const [loading, setLoading] = useState<boolean>(false);

  const { user } = useAuthentication();

  const onSubmit = async (updatedCompany: CompanyAWS) => {
    const differences = getObjectDifferences(company, updatedCompany);
    try {
      setLoading(true);
      const changelogUpdate = axios.put('/dealer/meta-data/changelog', { pk: company.pk, changes: { ...differences, user } });
      const metaDataUpdate = axios.put('/dealer/meta-data', updatedCompany);
      const [changeLogResult, metaDataUpdateResult] = await Promise.all([changelogUpdate, metaDataUpdate]);
      toast.success(`Company [${company.attributes.companyName}] attributes updated!`);
      closeModal();
    } catch (error) {
      console.log(error);
      toast.error('Server Error');
    } finally {
      setLoading(false);
    }
  }

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  };

  const editAttributesSchema: yup.ObjectSchema<CompanyAWS> = yup.object().shape({
    pk: yup.string().required(),
    sk: yup.string().required(),
    attributes: yup.object<CompanyAttributes>().shape({
      dealerId: yup.string().optional(),
      companyName: yup.string().required(),
      active: yup.boolean().required(),
      international: yup.boolean().required(),
      customVehiclePricing: yup.boolean().required(),
      freightChargesDisabled: yup.boolean().required(),
      dateCreated: yup.string().optional(),
      priorityDealer: yup.boolean().required(),
      allowLeads: yup.boolean().required(),
      allowLeadsBySeriesType: yup.array().optional().of(
        yup.string().oneOf([...Object.values(ChassisSeriesTypes)]).required()
      ).min(1, 'Please select at least one chassis series'),
      logo: yup.string().optional(),
      licenses: yup.array().optional().of(
        yup.object<License>().shape({
          status: yup.string().oneOf([...LicenseStatusTypes]).required(),
          state: yup.string().required(),
          stateCode: yup.string().required(),
          type: yup.string().oneOf([...LicenseTypes]).required().default('LSV'),
          expirationDate: yup.string().required()
        })
      )
    })
  });

  const defaultValues = { ...company };

  return (
    <>
      {isVisible &&
        <Modal title={`Edit Attributes [${company.attributes.companyName}]`} closeModal={closeModal}>
          <Form onSubmit={onSubmit} onError={onError} resolver={yupResolver(editAttributesSchema)} defaultValues={defaultValues} keepValuesOnReset={true}>
            {(methods) => {
              const leadsAllowed = methods.watch('attributes.allowLeads');
              return (
                <FormProvider {...methods}>
                  <Stack
                    spacing={2}
                    direction="column"
                    alignItems="flex-end">
                    <TextField name="attributes.companyName" label="Company Name" defaultValue={company.attributes.companyName} />
                    <CheckBox
                      name="attributes.active"
                      label="Active"
                      isTrueFalse={true}
                      defaultValue={0} />
                    <CheckBox
                      name="attributes.allowLeads"
                      label="Allow Leads"
                      isTrueFalse={true}
                      defaultValue={0} />
                    {leadsAllowed &&
                      <Stack direction="column" width="100%" alignItems="flex-end" spacing={2}>
                        <HorizontalRule width="100%" />
                        <Typography>Select chassis series below supported by the dealer for lead generation</Typography>
                        <ChassisSeriesTypeOptions name="attributes.allowLeadsBySeriesType" />
                        <HorizontalRule />
                      </Stack>
                    }
                    <CheckBox
                      name="attributes.priorityDealer"
                      label="Lead Priority Dealer"
                      isTrueFalse={true}
                      defaultValue={0} />
                    <CheckBox
                      name="attributes.customVehiclePricing"
                      label="Custom Vehicle Pricing (must first be setup by MadJax web dev team)"
                      isTrueFalse={true}
                      defaultValue={0} />
                    <CheckBox
                      name="attributes.freightChargesDisabled"
                      label="Remove Freight Charges (removes freight charges for dealer cart purchases)"
                      isTrueFalse={true}
                      defaultValue={0} />
                    <CheckBox
                      name="attributes.international"
                      label="International Dealer"
                      isTrueFalse={true}
                      defaultValue={0} />
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={2}>
                      <LoadingButton loading={loading} type="submit">Submit</LoadingButton>
                      <Button onClick={closeModal} type="button">Cancel</Button>
                    </Stack>
                  </Stack>
                </FormProvider>
              )
            }}
          </Form>
        </Modal>
      }
    </>
  );
}

export default EditAttributesModal;
